import { DynamicField, FormMeta } from '../models';

export type JobCategory = 'normal' | 'oc' | 'early' | 'is';

export const JOB_AZ_CHECK_CATEGORY_OPTIONS: { value: JobCategory; label: string }[] = [
  { value: 'normal', label: '通常求人' },
  { value: 'oc', label: 'OC求人' },
  { value: 'early', label: '早期動員課金求人' },
  { value: 'is', label: 'IS求人' },
];

// AzCheckエラー詳細のfieldキーに付与されている接頭辞（mach-api の jobAzCheck.const.ts と対応。通常項目のfieldKeyと衝突しないよう対象種別ごとに割り当てている）
export const ENTERPRISE_PREFIX = 'enterprise_';
export const SALARY_TABLE_FIELD_KEY_PREFIX = 'salaryTable_';
export const WAGE_PREFIX = 'wage_';

// セミナー・面接情報の集約チェックのfieldKey（mach-api の AZ_CHECK_FIELDS_BY_JOB_CATEGORY と対応）
export const SEMINAR_AND_INTERVIEW_INFO_FIELD_KEY = 'seminarAndInterviewInfo';

// 「種別」区分（大学院卒〜高校卒）。job.dynamicData.salaryTable のキーは `項目_卒業種別` の形式で構成する。
export type GradCategory =
  | 'noCategory'
  | 'postGraduateGrad'
  | 'universityGrad'
  | 'juniorCollegeGrad'
  | 'kosenGrad'
  | 'senmonGrad'
  | 'highSchoolGrad';

export interface GradCategoryOption {
  gradCategory: GradCategory;
  label: string;
}

// 種別追加ダイアログの種別選択肢（種別なし(noCategory)は選択肢に含めない。未選択時はnoCategory扱いにする）
export const GRAD_CATEGORY_OPTIONS: GradCategoryOption[] = [
  { gradCategory: 'postGraduateGrad', label: '大学院卒' },
  { gradCategory: 'universityGrad', label: '大学卒' },
  { gradCategory: 'juniorCollegeGrad', label: '短大卒' },
  { gradCategory: 'kosenGrad', label: '高専卒' },
  { gradCategory: 'senmonGrad', label: '専門卒' },
  { gradCategory: 'highSchoolGrad', label: '高校卒' },
];

// 種別なしを含む全ての卒業種別（noCategoryを先頭に配置）
export const ALL_GRAD_CATEGORIES: GradCategory[] = ['noCategory', ...GRAD_CATEGORY_OPTIONS.map((option) => option.gradCategory)];

// job.dynamicData.salaryTable のキー接頭辞
export type SalaryTableItemPrefix =
  | 'salary_'
  | 'fixedOvertimeHours_'
  | 'fixedOvertimeAmount_'
  | 'fixedLateNightOvertimeHours_'
  | 'fixedLateNightOvertimeAmount_';

export const JOB_SALARY_TABLE_ITEM = {
  SALARY: 'salary',
  FIXED_OVERTIME_FEE: 'fixedOvertimeFee',
  FIXED_OVERTIME_DESCRIPTION: 'fixedOvertimeFeeDescription',
  FIXED_LATE_NIGHT_OVERTIME: 'fixedLateNightOvertime',
  VARIABLE_WORKING_HOURS_SYSTEM: 'variableWorkingHoursSystem',
  SALARY_DESCRIPTION: 'salaryDescription',
} as const;

export type JobSalaryItemValue = typeof JOB_SALARY_TABLE_ITEM[keyof typeof JOB_SALARY_TABLE_ITEM];

export interface AzCheckRow {
  fieldKey: string;
  fieldDefinition: DynamicField;
  formMeta: FormMeta;
  useCompanyModel?: boolean;
  // 資本金・売上高等の非公開／グループ連結チェックボックスのように、この項目の値と併せて表示すべき項目のfieldName一覧
  companionFieldNames?: string[];
}

export interface AzCheckSection {
  title: string;
  rows: AzCheckRow[];
}
export interface JobAzCheckErrorDetail {
  // チェック項目
  // 同じ項目があると名前が衝突してしまうため、以下のprefixをつけて返却される
  // 求人：prefixなし
  // 企業；ENTERPRISE_PREFIX (enterprise_)
  // salaryTable：SALARY_TABLE_FIELD_KEY_PREFIX (salaryTable_)
  field: string;
  target: 'job' | 'enterprise';
  type: 'error' | 'warning';
  isDisplayForCompany: boolean;
  message: string;
  messageForCompany: string;
}

// 賃金チェックの計算式を表す1ステップ分の内訳
export interface JobAzCheckWageCalculationStep {
  label: string;
  operands: string;
  result: string;
  unit: string;
  description: string;
}

// 賃金チェックのエラー詳細
export interface JobAzCheckWageErrorDetail {
  // チェック項目
  // 同じ項目があると名前が衝突してしまうため、接頭辞にWAGE_PREFIX、接尾辞にgradCategory付きで返却される
  // 例：wage_minWage_universityGrad
  field: string;
  gradCategory: GradCategory | null;
  type: 'error';
  isDisplayForCompany: boolean;
  message: string;
  messageForCompany: string;
  requiredValueLabel: string;
  requiredValue: number | null;
  actualValueLabel: string;
  actualValue: number | null;
  unit: string;
  calculationSteps: JobAzCheckWageCalculationStep[];
}

export interface JobAzCheckResponse {
  totalFieldsCount: number;
  validationErrors: {
    details: JobAzCheckErrorDetail[];
    salaryTableDetails: JobAzCheckErrorDetail[];
    wageDetails: JobAzCheckWageErrorDetail[];
  };
}

export type WageCheckField = 'minWage' | 'fixedOvertimeFee' | 'monthlyScheduledWorkingHours' | 'breakHour';

export const WAGE_CHECK_FIELDS: WageCheckField[] = ['minWage', 'fixedOvertimeFee', 'monthlyScheduledWorkingHours', 'breakHour'];

export const WAGE_CHECK_FIELD_LABELS: { [key in WageCheckField]: string } = {
  minWage: '最低賃金',
  fixedOvertimeFee: '固定残業代',
  monthlyScheduledWorkingHours: '月間所定労働時間',
  breakHour: '休憩時間',
};

export interface JobAzCheckConsultingItem {
  consultingTargetKey: string;
  // 相談項目の表示整形（mach-api側）の分岐に使うフィールド種別
  fieldName: string;
  label: string;
  // DynamicDataの生値
  value?: any;
  visibleItems?: string[];
  consultingContent: string;
  errorType: 'error' | 'warning';
  errorMessage: string;
  unit?: string;
  // 資本金・売上高等の非公開／グループ連結チェックボックスのように、値と併せて表示したい情報（例：「非公開」「グループ連結」）
  companionText?: string;
  // 以下賃金チェック項目（fieldName: 'wageCheck'）のみ設定される
  requiredValueLabel?: string;
  actualValueLabel?: string;
  requiredValue?: number | null;
  actualValue?: number | null;
}

// AZチェックのタイミング（求人作成時 or AZ前 or 募集中ルートの企業送信前・企業送信後・企業からの相談内容）。
export type JobAzCheckTiming = 'onCreate' | 'beforeAz' | 'afterAz1st' | 'afterAz2nd' | 'onCompanyCheck' | 'afterAzOnCompanyCheck';

// タイミングと job.dynamicData に保存されている企画相談内容のキー名の対応
export const JOB_AZ_CHECK_CONSULTING_INFO_DYNAMIC_DATA_KEY: { [key in JobAzCheckTiming]: string } = {
  onCreate: 'azConsultingInfoOnCreate',
  onCompanyCheck: 'azConsultingInfoOnCompanyCheck',
  beforeAz: 'azConsultingInfoBeforeAz',
  afterAz1st: 'azConsultingInfoAfterAz1st',
  afterAz2nd: 'azConsultingInfoAfterAz2nd',
  afterAzOnCompanyCheck: 'azConsultingInfoAfterAzOnCompanyCheck',
};

// 募集中フロー（AZ後チェック）の対象となる求人ステータス
export const AFTER_AZ_FLOW_STATUSES: string[] = ['募集中', '推薦停止', '終了'];

export interface JobAzCheckConsultingInfo {
  savedTiming: JobAzCheckTiming;
  selectedJobCategory: JobCategory | null;
  contactMessage: string;
  consultingItems: { [consultingTargetKey: string]: JobAzCheckConsultingItem };
  middlePermissionFlg: 0 | 1 | null;
  resultDescription: string;
}
